import type { DraggableAttributes, DraggableSyntheticListeners } from "@dnd-kit/core";
import { AnchoredPanel } from "@bafe/ui";
import {
	useId,
	useRef,
	useState,
	type CSSProperties,
	type MouseEvent,
	type PointerEvent,
	type TouchEvent,
} from "react";

import {
	hubToneClass,
	hubToneStyle,
	resolveAssigneeColorValue,
	resolveTagColorValue,
} from "./hub-task-colors.js";
import type { HubTask } from "./task-board-order.js";

type TaskBoardStatus = "new" | "pending" | "reviewed" | "completed";

export type TaskDragSlotSize = {
	width: number;
	height: number;
};

const TASK_DRAG_PILL_STYLE: CSSProperties = {
	display: "inline-flex",
	alignItems: "center",
	justifyContent: "center",
	boxSizing: "border-box",
	minWidth: 80,
	height: 40,
	maxWidth: "min(220px, calc(100vw - 28px))",
	padding: "0 16px",
	borderRadius: 999,
	border: "1.5px solid #d4d4d8",
	background: "#ffffff",
	color: "#18181b",
	boxShadow:
		"0 0 0 1px #ffffff, 0 8px 20px rgba(9, 9, 11, 0.14), 0 2px 8px rgba(9, 9, 11, 0.08)",
	pointerEvents: "none",
};

function taskAssigneeNames(assignee: string): string[] {
	const seen = new Set<string>();
	const names: string[] = [];
	for (const item of assignee.split(",")) {
		const name = item.trim();
		if (!name) continue;
		const key = name.toLowerCase();
		if (seen.has(key)) continue;
		seen.add(key);
		names.push(name);
	}
	return names;
}

const TASK_CARD_AVATAR_LIMIT = 4;

function personInitials(name: string): string {
	const trimmed = name.trim();
	const local = trimmed.includes("@") ? (trimmed.split("@")[0] ?? trimmed) : trimmed;
	const parts = local.replace(/[()]/g, " ").split(/\s+/).filter(Boolean);
	const first = parts[0];
	const second = parts[1];
	if (first && second) return `${first.slice(0, 1)}${second.slice(0, 1)}`.toUpperCase();
	return (first?.slice(0, 2) || "?").toUpperCase();
}

function personLabel(name: string): string {
	return name.trim().split(/\s+/).map((part) => {
		if (!part || part.includes("@") || part.startsWith("(")) return part;
		return part.charAt(0).toUpperCase() + part.slice(1);
	}).join(" ");
}

function AssigneeAvatar({
	name,
	assigneeColorByLowerName,
}: {
	name: string;
	assigneeColorByLowerName: ReadonlyMap<string, string | undefined>;
}) {
	const colorValue = resolveAssigneeColorValue(name, assigneeColorByLowerName);
	return (
		<span
			className={`task-card-assignee-avatar ${hubToneClass(colorValue, name)}`}
			style={hubToneStyle(colorValue, name)}
			aria-hidden="true"
		>
			{personInitials(name)}
		</span>
	);
}

function TaskCardAssignees({
	names,
	assigneeLabel,
	assigneeColorByLowerName,
	interactive,
}: {
	names: string[];
	assigneeLabel: string;
	assigneeColorByLowerName: ReadonlyMap<string, string | undefined>;
	interactive: boolean;
}) {
	const [open, setOpen] = useState(false);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const panelId = useId();
	const title = assigneeLabel || names.map(personLabel).join(", ");
	const visible = names.slice(0, TASK_CARD_AVATAR_LIMIT);
	const overflow = names.length - visible.length;

	const faces = (
		<>
			{visible.map((name) => (
				<AssigneeAvatar
					key={name.toLowerCase()}
					name={name}
					assigneeColorByLowerName={assigneeColorByLowerName}
				/>
			))}
			{overflow > 0 ? (
				<span className="task-card-assignee-avatar task-card-assignee-overflow hub-tone-zinc" aria-hidden="true">
					+{overflow}
				</span>
			) : null}
		</>
	);

	if (!interactive) {
		return <div className="task-card-assignees is-avatars">{faces}</div>;
	}

	const keepOnStack = (event: MouseEvent | PointerEvent | TouchEvent) => {
		event.stopPropagation();
	};

	return (
		<>
			<button
				ref={triggerRef}
				type="button"
				className="task-card-assignees is-avatars"
				title={title}
				aria-label={`Assigned to ${title}`}
				aria-haspopup="dialog"
				aria-expanded={open}
				aria-controls={panelId}
				onPointerDown={keepOnStack}
				onTouchStart={keepOnStack}
				onKeyDown={(event) => {
					event.stopPropagation();
					if (event.key === "Escape" && open) {
						event.preventDefault();
						setOpen(false);
					}
				}}
				onClick={(event) => {
					event.stopPropagation();
					setOpen((current) => !current);
				}}
			>
				{faces}
			</button>
			<AnchoredPanel
				open={open}
				anchorRef={triggerRef}
				align="start"
				onDismiss={() => setOpen(false)}
				id={panelId}
				role="dialog"
				aria-label={`Assigned to ${title}`}
				className="task-card-assignee-popover min-w-48 max-w-64 p-2"
				onKeyDown={(event) => {
					if (event.key !== "Escape") return;
					event.preventDefault();
					setOpen(false);
					triggerRef.current?.focus();
				}}
			>
				<p className="px-1.5 pb-1 text-xs text-(--text-muted)">Assigned</p>
				<ul>
					{names.map((name) => (
						<li key={name.toLowerCase()} className="flex items-center gap-2 px-1.5 py-1">
							<AssigneeAvatar name={name} assigneeColorByLowerName={assigneeColorByLowerName} />
							<span className="min-w-0 truncate text-sm text-(--text-primary)">{personLabel(name)}</span>
						</li>
					))}
				</ul>
			</AnchoredPanel>
		</>
	);
}

const TASK_DRAG_PILL_TITLE_STYLE: CSSProperties = {
	minWidth: 0,
	margin: 0,
	fontSize: 14,
	fontWeight: 500,
	lineHeight: 1.2,
	letterSpacing: "-0.01em",
	whiteSpace: "nowrap",
	overflow: "hidden",
	textOverflow: "ellipsis",
};

export function TaskDragPill({ title }: { title: string }) {
	return (
		<div className="task-drag-pill" role="presentation" style={TASK_DRAG_PILL_STYLE}>
			<span className="task-drag-pill-title" style={TASK_DRAG_PILL_TITLE_STYLE}>
				{title}
			</span>
		</div>
	);
}

export function TaskDropPlaceholder({ size }: { size?: TaskDragSlotSize }) {
	const style: CSSProperties | undefined = size?.height
		? {
				width: "100%",
				height: size.height,
				minHeight: size.height,
				maxHeight: size.height,
				flex: "0 0 auto",
				boxSizing: "border-box",
			}
		: undefined;
	return (
		<article
			className="card task-card task-drop-placeholder"
			style={style}
			aria-hidden="true"
		/>
	);
}

/** Empty column footprint — matches a minimal task card (strip, title, created). */
export function TaskColumnEmptyCard({ status }: { status: TaskBoardStatus }) {
	return (
		<article
			className={`card task-card status-${status} task-card-column-empty`}
			data-task-card-slot="empty"
			aria-label="Empty column"
		>
			<div className="task-card-face">
				<p className="card-title task-card-column-empty-message">Nothing here.</p>
				<div className="task-card-meta task-card-badges task-card-column-empty-skeleton" aria-hidden="true">
					<div className="task-card-dates-stack">
						<span className="task-card-date-plain">
							<span className="task-card-date-plain-kicker">Created</span>
							<span className="task-card-date-plain-value">—</span>
						</span>
					</div>
				</div>
			</div>
		</article>
	);
}

export function TaskBoardCard({
	task,
	status,
	tagList,
	checklistDone,
	checklistTotal,
	attachmentCount,
	coverImageUrl,
	overdue,
	createdLabel: _createdLabel,
	assigneeLabel,
	assigneeKey: _assigneeKey,
	tagColorByLowerName,
	assigneeColorByLowerName,
	dueLabel,
	dueLabelCompact,
	cardHint,
	sortableRef,
	sortableStyle,
	dragAttributes,
	dragListeners,
	isSortableDragging,
	isDraggable,
	enableMobileSwipe,
	onSwipeStatus,
	onOpen,
	onMenu,
	dragOverlay,
}: {
	task: HubTask;
	status: TaskBoardStatus;
	tagList: string[];
	checklistDone: number;
	checklistTotal: number;
	attachmentCount: number;
	coverImageUrl: string;
	overdue: boolean;
	createdLabel: string;
	assigneeLabel: string;
	assigneeKey: string;
	tagColorByLowerName: ReadonlyMap<string, string | undefined>;
	assigneeColorByLowerName: ReadonlyMap<string, string | undefined>;
	dueLabel: string;
	dueLabelCompact: string;
	cardHint: string;
	sortableRef?: (node: HTMLElement | null) => void;
	sortableStyle?: CSSProperties;
	dragAttributes?: DraggableAttributes;
	dragListeners?: DraggableSyntheticListeners;
	isSortableDragging?: boolean;
	isDraggable?: boolean;
	enableMobileSwipe?: boolean;
	onSwipeStatus?: (direction: -1 | 1) => void;
	onOpen: () => void;
	onMenu: (event: MouseEvent) => void;
	dragOverlay?: boolean;
}) {
	const skipOpenClick = useRef(false);
	const swipeTouch = useRef<{ x: number; y: number; t: number } | null>(null);
	const coverSrc = coverImageUrl.trim();
	const showAttachmentBadge = Boolean(
		(attachmentCount && !coverImageUrl) || (attachmentCount > 1 && coverImageUrl),
	);
	const showDueFace = Boolean(dueLabel);
	const showMetaIcons = showAttachmentBadge || checklistTotal > 0;
	const assigneeNames = taskAssigneeNames(task.assignee ?? "");
	const showMetaBottom = Boolean(
		assigneeNames.length || showAttachmentBadge || checklistTotal || showDueFace,
	);
	const tagPreview = tagList.slice(0, 3);
	const tagOverflow = tagList.length - tagPreview.length;
	const showTags = tagPreview.length > 0;

	const onTouchStart = (event: TouchEvent<HTMLElement>) => {
		if (!enableMobileSwipe || !onSwipeStatus) return;
		const touch = event.touches[0];
		if (!touch) return;
		swipeTouch.current = { x: touch.clientX, y: touch.clientY, t: Date.now() };
	};

	const onTouchEnd = (event: TouchEvent<HTMLElement>) => {
		if (!enableMobileSwipe || !onSwipeStatus) return;
		const start = swipeTouch.current;
		swipeTouch.current = null;
		if (!start) return;
		const touch = event.changedTouches[0];
		if (!touch) return;
		const dx = touch.clientX - start.x;
		const dy = touch.clientY - start.y;
		const elapsed = Date.now() - start.t;
		// Long-press drag uses ~260ms; swipes must stay quick flicks.
		if (elapsed > 320) return;
		if (Math.abs(dx) < 52 || Math.abs(dx) < Math.abs(dy) * 1.2) return;
		skipOpenClick.current = true;
		onSwipeStatus(dx < 0 ? 1 : -1);
	};

	return (
		<article
			ref={sortableRef}
			style={sortableStyle}
			className={[
				"card task-card",
				`status-${status}`,
				coverSrc ? "has-cover" : "",
				isDraggable ? "is-draggable" : "",
				isSortableDragging ? "is-sortable-dragging" : "",
				dragOverlay ? "is-drag-overlay" : "",
			].filter(Boolean).join(" ")}
			role="button"
			tabIndex={0}
			aria-label={task.title}
			data-task-id={task.id || undefined}
			{...dragAttributes}
			{...dragListeners}
			onTouchStart={onTouchStart}
			onTouchEnd={onTouchEnd}
			onClick={() => {
				if (skipOpenClick.current) {
					skipOpenClick.current = false;
					return;
				}
				onOpen();
			}}
			onContextMenu={onMenu}
			onKeyDown={(event) => {
				if (event.key === "Enter" || event.key === " ") {
					event.preventDefault();
					onOpen();
				}
			}}
		>
			{cardHint ? <span className="sr-only">{cardHint}</span> : null}
			{coverSrc ? (
				<div className="task-card-cover" aria-hidden="true">
					<img className="task-card-cover-img" src={coverSrc} alt="" decoding="async" />
				</div>
			) : null}
			<div className="task-card-face">
				<p className="card-title">{task.title}</p>
				{showTags ? (
					<div className="task-card-chips" aria-label="Tags">
						{tagPreview.map((name) => (
							<span
								className={`task-card-tag ${hubToneClass(resolveTagColorValue(name, tagColorByLowerName), name)}`}
								style={hubToneStyle(resolveTagColorValue(name, tagColorByLowerName), name)}
								key={name}
								title={name}
							>
								{name}
							</span>
						))}
						{tagOverflow > 0 ? (
							<span className="task-card-tag task-card-tag-more hub-tone-zinc" title={tagList.slice(3).join(", ")}>
								+{tagOverflow}
							</span>
						) : null}
					</div>
				) : null}
				{showMetaBottom ? (
					<div
						className={[
							"task-card-meta-bottom",
							showMetaIcons ? "" : "is-trailing-due-only",
						].filter(Boolean).join(" ")}
					>
						{assigneeNames.length ? (
							<TaskCardAssignees
								names={assigneeNames}
								assigneeLabel={assigneeLabel}
								assigneeColorByLowerName={assigneeColorByLowerName}
								interactive={!dragOverlay}
							/>
						) : (
							<span className="task-card-assignee-name task-card-assignee-empty" aria-hidden="true" />
						)}
						{showAttachmentBadge || checklistTotal || showDueFace ? (
							<div className="task-card-meta-trailing">
								{showAttachmentBadge || checklistTotal ? (
									<div className="task-card-meta-icons">
										{showAttachmentBadge ? (
											<span className="task-card-icon task-card-attach">{attachmentCount}</span>
										) : null}
										{checklistTotal ? (
											<span className="task-card-icon task-card-check">{checklistDone}/{checklistTotal}</span>
										) : null}
									</div>
								) : null}
								{showDueFace ? (
									<span
										className={`task-card-due${overdue ? " is-overdue" : ""}`}
										title={dueLabel}
										aria-label={overdue ? `Overdue, due ${dueLabel}` : `Due ${dueLabel}`}
									>
										<span className="task-card-due-icon" aria-hidden="true" />
										<span className="task-card-due-value">{dueLabelCompact || dueLabel}</span>
									</span>
								) : null}
							</div>
						) : null}
					</div>
				) : null}
			</div>
		</article>
	);
}
